import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Center,
  Checkbox,
  CheckboxGroup,
  Flex,
  FormControl,
  Input,
  InputGroup,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalOverlay,
  Text,
  useMediaQuery,
  useTheme,
  useToast,
  VStack,
} from '@chakra-ui/react';
import Recaptcha from 'react-google-recaptcha';
import * as yup from 'yup';
import axios from 'axios';
import { yupResolver } from '@hookform/resolvers/yup';
import { type SubmitHandler, useForm } from 'react-hook-form';
import { AnimatePresence, motion } from 'framer-motion';
import hiddenMavenoid from '@repo/ui-components/dept/utils/hiddenMavenoid';
import Link from '../../../tailwind/Link';
import { useUiComponentsInternationalContext } from '@repo/static-translations';
import { LOCALE_REPLACEMENT_FOR_RECAPTCHA } from '@repo/gatsby-localization-helpers';
import { localeToKlaviyoPublicMap } from '../../../../utils/klaviyo';

type NewsletterModalProps = {
  isOpen: boolean;
  onClose: () => void;
};

export default function NewsletterModal({
  isOpen,
  onClose,
}: Readonly<NewsletterModalProps>) {
  const recaptchaRef = useRef<Recaptcha | null>(null);
  const { tt, locale } = useUiComponentsInternationalContext();

  const recaptchaLocale = LOCALE_REPLACEMENT_FOR_RECAPTCHA?.[locale] ?? locale;

  const validationSchema = useMemo(
    () =>
      yup.object({
        userEmail: yup
          .string()
          .email(tt(s => s.newsletterModalComponent.emailValidationMessage))
          .required(
            tt(s => s.newsletterModalComponent.emailRequiredValidationMessage)
          ),
        firstName: yup
          .string()
          .required(
            tt(
              s => s.newsletterModalComponent.firstNameRequiredValidationMessage
            )
          ),
        options: yup.array().min(1).of(yup.string().required()).required(),
      }),
    [tt]
  );

  type FormData = yup.InferType<typeof validationSchema>;

  const checkboxOptions = useMemo(
    () => [
      tt(s => s.newsletterModalComponent.catOption),
      tt(s => s.newsletterModalComponent.dogOption),
    ],
    [tt]
  );

  const toast = useToast();
  const theme = useTheme();
  const [isLgScreen] = useMediaQuery(`(min-width: ${theme.breakpoints.lg})`);

  const [isClosing, setIsClosing] = useState(false);
  const [lambdaErrors, setLambdaErrors] = useState(null);
  const [submitDisabled, setSubmitDisabled] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleClose = () => {
    setIsClosing(false);
    onClose();
  };

  const handleDrag = (_, info) => {
    if (info.offset.y > 500) {
      setIsClosing(true);
    }
    handleClose();
  };

  useEffect(() => {
    hiddenMavenoid(isOpen);
  }, [isOpen]);

  const {
    register,
    handleSubmit,
    setFocus,
    formState: { errors },
    reset,
  } = useForm<FormData>({
    resolver: yupResolver(validationSchema),
  });

  useEffect(() => {
    setTimeout(() => {
      setFocus('firstName');
    }, 300);
  }, [isOpen]);

  const handleEmailSignup: SubmitHandler<FormData> = async (data, event) => {
    event.preventDefault();
    setSubmitDisabled(true);
    setIsLoading(true);

    const gRecaptchaResponse = recaptchaRef.current
      ? await recaptchaRef.current.execute()
      : null;
    if (!gRecaptchaResponse) return;

    try {
      await axios.request({
        url: 'https://a.klaviyo.com/client/subscriptions',
        params: {
          company_id: localeToKlaviyoPublicMap(
            locale,
            process.env.GATSBY_KLAVIYO_BRAND
          ).publicKey,
        },
        method: 'POST',
        headers: {
          accept: 'application/json',
          revision: '2024-05-15',
          'content-type': 'application/json',
        },
        data: JSON.stringify({
          data: {
            type: 'subscription',
            attributes: {
              custom_source: 'Newsletter modal',
              profile: {
                data: {
                  type: 'profile',
                  attributes: {
                    email: data.userEmail,
                    first_name: data.firstName,
                    properties: {
                      brand: 'petsafe',
                      interests: data.options.join(','),
                    },
                  },
                },
              },
            },
            relationships: {
              list: {
                data: {
                  type: 'list',
                  id: localeToKlaviyoPublicMap(
                    locale,
                    process.env.GATSBY_KLAVIYO_BRAND
                  ).newsletterListId,
                },
              },
            },
          },
        }),
      });

      setSubmitDisabled(false);
      setIsLoading(false);

      toast({
        title: tt(s => s.newsletterModalComponent.thanksForSignToastTitle),
        description: (
          <VStack align="start" gap={0}>
            <Text mb={4}>
              {tt(s => s.newsletterModalComponent.checkInboxToastDescription)}
            </Text>
            <Text fontWeight="bold">
              {tt(s => s.newsletterModalComponent.importantNoticeToastLabel)}
            </Text>
            <Text>
              {tt(s => s.newsletterModalComponent.checkSpamFolderToastLabel)}
            </Text>
          </VStack>
        ),
        status: 'success',
        duration: 9000,
        isClosable: true,
        position: 'top',
        variant: 'success',
      });

      reset();
      handleClose();
    } catch (error) {
      console.error(error);
      const errorMessage = tt(s => s.newsletterModalComponent.errorMessage);
      setLambdaErrors(errorMessage);
      toast({
        title: tt(s => s.newsletterModalComponent.signUpUnsuccessfulMessage),
        description: errorMessage,
        status: 'error',
        duration: 9000,
        isClosable: true,
        position: 'top',
      });
    }
    setSubmitDisabled(false);
    setIsLoading(false);
  };

  return (
    <AnimatePresence>
      <Modal isCentered isOpen={isOpen} onClose={handleClose}>
        <ModalOverlay zIndex={5} />
        {isOpen && (
          <Box
            as={motion.div}
            style={{
              position: 'fixed',
              bottom: 0,
              left: 0,
              right: 0,
              top: isClosing ? '100%' : 0,
            }}
            zIndex={999}
            initial={!isLgScreen && { y: '100%' }}
            w={{ base: '100vw', lg: 'auto' }}
            animate={
              !isLgScreen &&
              (isClosing
                ? { y: '100%', transition: { duration: 0.5 } }
                : { y: '15%', transition: { duration: 0.5 } })
            }
            exit={!isLgScreen && { y: '100%' }}
            drag="y"
            onDragEnd={handleDrag}
          >
            <ModalContent
              w="500px"
              borderRadius={['24px 24px 0px 0px', null, 'lg']}
            >
              <ModalCloseButton
                borderColor="stroke.base"
                borderRadius="lg"
                borderWidth="1px"
                w="40px"
                h="40px"
                top="6"
                right="6"
                zIndex={1}
                display={['none', null, 'block']}
              />
              <ModalBody
                pb={['5.5rem', null, '10']}
                px={[4, null, '10']}
                pt={[4, null, '10']}
              >
                <Box
                  h="6px"
                  w="68px"
                  borderRadius="10px"
                  backgroundColor="#D9D9D9"
                  pos="absolute"
                  left="50%"
                  transform="translate(-50%, -50%)"
                  display={['block', null, 'none']}
                />
                <FormControl
                  as="form"
                  onSubmit={handleSubmit(handleEmailSignup)}
                >
                  <VStack align="flex-start">
                    <Text textStyle="h4_5_6" fontWeight="bold" mb={4} mt={6}>
                      {tt(s => s.newsletterModalComponent.emailUpdatesLabel)}
                    </Text>

                    <InputGroup mb="4" as={VStack} align="flex-start">
                      <Input
                        h={12}
                        pr={4}
                        type="text"
                        placeholder={tt(
                          s =>
                            s.newsletterModalComponent.firstNamePlaceholderLabel
                        )}
                        borderRadius="xl"
                        border="1px solid"
                        fontSize={16}
                        fontWeight="light"
                        letterSpacing="tight"
                        isInvalid={!!errors.firstName}
                        errorBorderColor="alerts.danger.base"
                        {...register('firstName')}
                      />
                      {errors.firstName && (
                        <Text
                          pos="relative"
                          left="2"
                          color="alerts.danger.base"
                          textStyle="caption"
                        >
                          {errors.firstName.message}
                        </Text>
                      )}
                    </InputGroup>

                    <InputGroup mb="4" as={VStack} align="flex-start">
                      <Input
                        h={12}
                        pr={4}
                        type="text"
                        placeholder={tt(
                          s => s.newsletterModalComponent.emailPlaceholderLabel
                        )}
                        borderRadius="xl"
                        border="1px solid"
                        fontSize={16}
                        fontWeight="light"
                        letterSpacing="tight"
                        isInvalid={!!errors.userEmail}
                        errorBorderColor="alerts.danger.base"
                        {...register('userEmail')}
                      />
                      {errors.userEmail && (
                        <Text
                          pos="relative"
                          left="2"
                          color="alerts.danger.base"
                          textStyle="caption"
                        >
                          {errors.userEmail.message}
                        </Text>
                      )}
                    </InputGroup>
                    <Text textStyle="body">
                      {tt(s => s.newsletterModalComponent.sendInformationLabel)}
                    </Text>
                    <CheckboxGroup defaultValue={checkboxOptions}>
                      <Flex justify="flex-start" w="full" pl="4" mb="6">
                        {checkboxOptions.map(c => (
                          <Checkbox
                            key={c}
                            value={c}
                            mr="6"
                            id={c}
                            isInvalid={!!errors.options}
                            {...register('options')}
                          >
                            {c}
                          </Checkbox>
                        ))}
                      </Flex>
                      {errors.options && (
                        <Text textStyle="caption" color="alerts.danger.base">
                          {tt(
                            s => s.newsletterModalComponent.selectInterestLabel
                          )}
                        </Text>
                      )}
                    </CheckboxGroup>
                    <Center w="full">
                      <Button
                        isLoading={isLoading}
                        loadingText="Submitting"
                        type="submit"
                        variant="secondary"
                        disabled={submitDisabled}
                      >
                        {tt(s => s.newsletterModalComponent.signUpButton)}
                      </Button>
                    </Center>
                    {lambdaErrors && (
                      <Text textStyle="caption" color="alerts.danger.base">
                        {lambdaErrors}
                      </Text>
                    )}
                    <Center w="full" mt="10" mb="4">
                      <Recaptcha
                        ref={recaptchaRef}
                        size="invisible"
                        sitekey={process.env.GATSBY_PETSAFE_RECAPTCHA_SITE_KEY}
                        badge="inline"
                        hl={recaptchaLocale}
                      />
                    </Center>
                    <Text textStyle="caption">
                      {tt(s => s.newsletterModalComponent.agreementText)}{' '}
                      <Text
                        as={Link}
                        className="underline"
                        link="/privacy-policy"
                      >
                        {tt(s => s.newsletterModalComponent.privacyPolicyLabel)}
                      </Text>
                      .
                    </Text>
                  </VStack>
                </FormControl>
              </ModalBody>
            </ModalContent>
          </Box>
        )}
      </Modal>
    </AnimatePresence>
  );
}
